import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/MobileScreener.css';

/**
 * Simplified preset card for intermediate screener mode
 * Shows preset name + value/description (single parameter)
 *
 * @param {string} presetKey - Unique identifier for the preset
 * @param {string} label - Preset display name (e.g., "Short-Term", "ATM")
 * @param {string} description - Value or description (e.g., "1-3 months", "+20%")
 * @param {string} tooltip - Optional tooltip text for info button
 * @param {string} sentiment - Optional sentiment for color coding ('bullish', 'bearish')
 * @param {boolean} isSelected - Whether this preset is currently selected
 * @param {function} onClick - Click handler for selection toggle
 */
const IntermediatePresetCard = ({
  presetKey,
  label,
  description,
  tooltip,
  sentiment,
  isSelected = false,
  onClick
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = () => {
    onClick(presetKey);
  };

  const handleInfoClick = (e) => {
    e.stopPropagation(); // Prevent card selection when clicking info
    setShowTooltip(!showTooltip);
  };

  // Determine sentiment class for color coding
  const getSentimentClass = () => {
    if (!sentiment) return '';
    return sentiment === 'bullish' ? 'sentiment-bullish' : 'sentiment-bearish';
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`intermediate-preset-card ${isSelected ? 'selected' : ''} ${getSentimentClass()}`}
        aria-pressed={isSelected}
      >
        <div className="preset-card-content">
          <span className="preset-card-label">{label}</span>
          <span className="preset-card-description">{description}</span>
        </div>

        {/* Info button */}
        {tooltip && (
          <div className="preset-info-wrapper">
            <div
              className="preset-info-button"
              onClick={handleInfoClick}
              onKeyDown={(e) => e.key === 'Enter' && handleInfoClick(e)}
              role="button"
              tabIndex={0}
              aria-label="Show information"
              title="Click for more info"
            >
              i
            </div>
          </div>
        )}
      </button>

      {/* Tooltip overlay portaled to body so a transformed ancestor cannot
          create a containing block for the position:fixed backdrop. */}
      {tooltip && showTooltip && createPortal(
        <div
          className="preset-tooltip-overlay"
          onClick={() => setShowTooltip(false)}
        >
          <div
            className="preset-tooltip-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="preset-tooltip-close"
              onClick={handleInfoClick}
              onKeyDown={(e) => e.key === 'Enter' && handleInfoClick(e)}
              role="button"
              tabIndex={0}
              aria-label="Close"
            >
              &times;
            </div>
            <p>{tooltip}</p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default IntermediatePresetCard;
